import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'お問い合わせ | おかやま健康製薬株式会社',
    description: 'おかやま健康製薬へのお問い合わせページ。製品に関するご質問、ご相談など、お気軽にお問い合わせください。電話、メール、お問い合わせフォームで受付中。',
    url: `${siteUrl}/contact`,
    inLanguage: 'ja-JP',
    isPartOf: {
      '@type': 'WebSite',
      name: 'おかやま健康製薬株式会社',
      url: siteUrl,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'ホーム',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'お問い合わせ',
          item: `${siteUrl}/contact`,
        },
      ],
    },
    mainEntity: {
      '@type': 'Organization',
      name: 'おかやま健康製薬株式会社',
      telephone: '+81-86-012-3456',
      email: 'info@okayama-health.co.jp',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '北区○○町1-2-3',
        addressLocality: '岡山市',
        addressRegion: '岡山県',
        postalCode: '700-0000',
        addressCountry: 'JP',
      },
    },
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      alert('必須項目を入力してください。');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('category', formData.category);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6ej4jaohb161tfd93p0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          category: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="お問い合わせ | 岡山の健康サプリメント | おかやま健康製薬株式会社"
        description="おかやま健康製薬へのお問い合わせページ。製品に関するご質問、ご相談など、お気軽にお問い合わせください。電話、メール、お問い合わせフォームで受付中。営業時間：9:00-18:00（平日）TEL: 086-012-3456"
        keywords="岡山,お問い合わせ,相談,サプリメント,おかやま健康製薬"
        canonical={`${siteUrl}/contact`}
        schema={contactSchema}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative h-[40vh] mt-16 lg:mt-20 bg-gradient-to-br from-[#4A90E2] to-[#2A6D8D] flex items-center justify-center">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/d351c84d594e1afe9fc80e1e12a7e228.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="relative text-center text-white px-4">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold">お問い合わせ</h1>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-[700px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
              製品に関するご質問、ご相談など、お気軽にお問い合わせください。
              <br />
              専門スタッフが丁寧にご対応いたします。
            </p>
          </div>

          <form
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl shadow-xl p-6 lg:p-10"
          >
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-bold text-[#1A4D6D] mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#4A90E2] focus:ring-2 focus:ring-[#4A90E2]/20 transition-all text-sm"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-bold text-[#1A4D6D] mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#4A90E2] focus:ring-2 focus:ring-[#4A90E2]/20 transition-all text-sm"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-bold text-[#1A4D6D] mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#4A90E2] focus:ring-2 focus:ring-[#4A90E2]/20 transition-all text-sm"
                  placeholder="090-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-bold text-[#1A4D6D] mb-2">
                  お問い合わせ種別
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#4A90E2] focus:ring-2 focus:ring-[#4A90E2]/20 transition-all text-sm cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="製品について">製品について</option>
                  <option value="購入方法について">購入方法について</option>
                  <option value="配送について">配送について</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-bold text-[#1A4D6D] mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#4A90E2] focus:ring-2 focus:ring-[#4A90E2]/20 transition-all resize-none text-sm"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length} / 500文字
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-4 bg-[#4A90E2] text-white rounded-lg font-bold hover:bg-[#2A6D8D] transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm lg:text-base"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
            </div>

            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 text-center">
                  お問い合わせを受け付けました。ご連絡ありがとうございます。
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 text-center">
                  送信に失敗しました。お手数ですが、もう一度お試しください。
                </p>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Phone Contact Section */}
      <section className="py-12 lg:py-16 bg-[#F5FAFE]">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl p-6 lg:p-10 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-[#4A90E2] text-white rounded-full text-3xl mx-auto mb-6">
              <i className="ri-phone-line"></i>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#1A4D6D] mb-4">
              お電話でのお問い合わせ
            </h2>
            <a
              href="tel:0860123456"
              className="text-3xl lg:text-4xl font-bold text-[#4A90E2] hover:text-[#2A6D8D] transition-colors mb-6 block cursor-pointer"
            >
              086-012-3456
            </a>
            <div className="space-y-2 text-sm lg:text-base text-gray-600">
              <p className="flex items-center justify-center gap-2">
                <i className="ri-time-line text-[#4A90E2]"></i>
                営業時間：9:00 - 18:00（平日）
              </p>
              <p className="flex items-center justify-center gap-2">
                <i className="ri-calendar-line text-[#4A90E2]"></i>
                定休日：土曜日・日曜日・祝日
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-[#E8F4F8] to-[#D0E8F2] rounded-2xl p-6 lg:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-[#4A90E2]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1A4D6D] mb-2">メールでのお問い合わせ</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    24時間受付中。2営業日以内にご返信いたします。
                  </p>
                  <a
                    href="mailto:info@okayama-health.co.jp"
                    className="text-sm text-[#4A90E2] hover:text-[#2A6D8D] font-medium"
                  >
                    info@okayama-health.co.jp
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#E8F4F8] to-[#D0E8F2] rounded-2xl p-6 lg:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-[#4A90E2]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1A4D6D] mb-2">所在地</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    〒700-0000
                    <br />
                    岡山県岡山市北区○○町1-2-3
                  </p>
                  <Link
                    to="/company"
                    className="text-sm text-[#4A90E2] hover:text-[#2A6D8D] font-medium"
                  >
                    アクセス情報を見る →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}